import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form } from "formik";
import classNames from "classnames";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import { chevronLeftIcon, crossIcon } from "../../assets/icons";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import {
  deleteApplication,
  selectSelectedApp,
  selectAppDeployStatus,
  selectApplicationDeploymentsTrack,
  selectRecentSelectedApp,
} from "./index";
import { actions } from "./applications.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import {
  ROUTES,
  REGEX,
  EMPTY_STRING,
  COLOR_TYPES,
  INDEX,
  UPLOAD_LOGO_SIZE,
  IMAGE_SIZE_EXCEED_MESSAGE,
  DEFAULT_LOGO_URL,
  VALIDATION_MESSAGES,
  TIME_TO_ALLOW_REDEPLOY,
  APP_DEPLOY_AT,
  DEPLOY_STATUSES,
  DEFAULT_BRAND_LOGO_URL,
  FRONTEND_INITIAL_STATUS,
  BACKEND_INITIAL_STATUS,
  ZERO,
} from "../../constants";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { convertCamelCaseToCapitalize, getBase64FromFile } from "../../helpers";
import { actions as alertActions } from "../../components/organisms/Alert/alert.reducers";
import { ReDeployButton } from "../../components/molecules";
import { fetchDataModels } from "../DataModels";
import Alert, { selectAlert } from "../../components/organisms/Alert";

const BRAND_LOGO = "logoWithBrand";

const DEFAULT_VALUES = {
  appName: EMPTY_STRING,
  logo: EMPTY_STRING,
  logoWithBrand: EMPTY_STRING,
  primaryColor: "#642ec8",
  secondaryColor: "#d4c3f5",
  contrast: "#d4c3f5",
};

const { APPLICATIONS } = ROUTES;
const {
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
} = REGEX;
const { setSelectedAppId, setAppPipelineStatus, setRecentlySelectedApp } =
  actions;
const { setSelectedModal, setItemToBeDeleted, setShouldShowDeploymentModal } =
  modalActions;
const { setSelectedTab } = breadCrumbActions;
const { DELETE_APPLICATION } = SPINNER_NAMES;
const { DELETE_MODAL } = MODALS;
const { FIRST } = INDEX;
const { setAlert } = alertActions;
const {
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  APPLICATION_NAME_IS_REQUIRED,
} = VALIDATION_MESSAGES;
const { IN_PROGRESS, SUCCESS } = DEPLOY_STATUSES;

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appId } = useParams();
  const scrollRef = useRef();

  const selectedApp = useSelector(selectSelectedApp);
  const appDeployStatus = useSelector(selectAppDeployStatus);
  const { deploymentStatus: deploymentLogs } = useSelector(
    selectApplicationDeploymentsTrack
  );
  const selectedAlert = useSelector(selectAlert);
  const recentSelectedApp = useSelector(selectRecentSelectedApp);

  const [appDetails, setAppDetails] = useState();
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowColorPicker, setShouldShowColorPicker] = useState(false);
  const [initialValues, setInitialValues] = useState(DEFAULT_VALUES);
  const [deployedAt, setDeployedAt] = useState(ZERO);
  const [timerActive, setTimerActive] = useState(true);
  const [show, setShow] = useState(false);
  const [data, setData] = useState();
  const [appLogoBase64, setAppLogoBase64] = useState(DEFAULT_LOGO_URL);
  const [brandLogoBase64, setBrandLogoBase64] = useState(
    DEFAULT_BRAND_LOGO_URL
  );
  const appDeploymentTime = JSON.parse(localStorage.getItem(APP_DEPLOY_AT));

  useEffect(() => {
    if (selectedApp) dispatch(setRecentlySelectedApp(selectedApp));
  }, [selectedApp, dispatch]);

  useEffect(() => {
    if (
      appDeployStatus?.frontend === SUCCESS &&
      appDeployStatus?.backend === SUCCESS &&
      appDeploymentTime
    )
      setDeployedAt(appDeploymentTime[selectedApp?.id]);
  }, [appDeploymentTime, selectedApp, appDeployStatus]);

  useEffect(() => {
    const timerInterval = setInterval(
      () => setTimerActive(+deployedAt + TIME_TO_ALLOW_REDEPLOY > Date.now()),
      1000
    );
    return () => clearInterval(timerInterval);
  }, [deployedAt]);

  const validationSchema = Yup.object({
    appName: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAIN)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(APPLICATION_NAME_IS_REQUIRED),
  });

  useEffect(() => {
    if (selectedAlert) scrollRef.current.scrollTop = 0;
  }, [selectedAlert]);

  useEffect(() => {
    if (appId && !selectedApp) dispatch(setSelectedAppId(appId));
  }, [dispatch, appId, selectedApp]);

  useEffect(() => () => dispatch(setAlert()), [dispatch]);

  useEffect(() => {
    if (selectedApp) {
      dispatch(setSelectedTab(selectedApp));
      setAppDetails(selectedApp);
    }
  }, [selectedApp, dispatch]);

  useEffect(() => {
    if (appDetails) {
      const {
        appName,
        logo,
        primaryColor,
        contrast,
        secondaryColor,
        logoWithBrand,
      } = appDetails.applicationJson.properties;
      setInitialValues({
        appName,
        logo,
        logoWithBrand,
        primaryColor,
        contrast,
        secondaryColor,
      });
    }
  }, [appDetails]);

  const navigateToPrevPage = () => navigate(-1);

  const handleDeleteApp = useCallback(() => {
    dispatch(deleteApplication(selectedApp.id)).then((res) => {
      if (res.payload) {
        dispatch(setSelectedModal(null));
        dispatch(fetchDataModels());
        setShouldShowDeleteModal(false);
        navigate(APPLICATIONS, { replace: true });
        if (recentSelectedApp?.id === selectedApp.id) {
          dispatch(setShouldShowDeploymentModal(false));
          dispatch(
            setAppPipelineStatus({
              frontend: FRONTEND_INITIAL_STATUS,
              backend: BACKEND_INITIAL_STATUS,
            })
          );
        }
      }
    });
  }, [dispatch, navigate, selectedApp, recentSelectedApp?.id]);

  const handleOnSubmit = (values) => {
    setShow(true);
    setData(values);
  };

  const isSubmitButtonDisabled = useMemo(() => {
    return appDeployStatus?.frontend === IN_PROGRESS;
  }, [appDeployStatus?.frontend]);

  const handleImageUpload = async (event, setFieldValue, name) => {
    const file = event.target.files[FIRST];
    if (file.size > UPLOAD_LOGO_SIZE) {
      dispatch(setAlert({ message: IMAGE_SIZE_EXCEED_MESSAGE }));
      return false;
    } else {
      const base64img = await getBase64FromFile(file);
      if (name === BRAND_LOGO) setBrandLogoBase64(base64img);
      else setAppLogoBase64(base64img);
      setFieldValue(name, base64img);
    }
  };

  const handleResetImage = (setFieldValue, type) => {
    const defaultLogo =
      type === BRAND_LOGO ? DEFAULT_BRAND_LOGO_URL : DEFAULT_LOGO_URL;
    if (type === BRAND_LOGO) setBrandLogoBase64(defaultLogo);
    else setAppLogoBase64(defaultLogo);
    setFieldValue(type, defaultLogo);
  };

  return (
    <div className="flex-1">
      <div
        className="scrollbar content overflow-auto p-6 flex-1"
        ref={scrollRef}
      >
        <Helmet>
          <title>Settings | TavaJS</title>
        </Helmet>
        <div className="flex-row xs:flex-row flex mb-4 gap-4">
          <button
            className="bg-gray-800 h-10 px-2 rounded-md hover:bg-gray-700/60"
            onClick={() => navigateToPrevPage()}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left icon" />
          </button>
        </div>
        {selectedAlert && (
          <div className="my-2 mx-3">
            <Alert
              type={selectedAlert.type}
              message={selectedAlert.message}
              handleOnClose={() => dispatch(setAlert())}
            />
          </div>
        )}
        <div className="border border-gray-700 mb-4 bg-gray-800 rounded-lg">
          <div className="p-6 border-b border-gray-700">
            <h6 className="text-base font-bold text-gray-50">App Settings</h6>
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleOnSubmit}
            validationSchema={validationSchema}
          >
            {({
              values,
              touched,
              handleChange,
              errors,
              isValid,
              dirty,
              setFieldValue,
            }) => {
              return (
                <Form>
                  <div className="p-6 form-group">
                    <div className="space-y-4 mb-6">
                      <div>
                        <label className="text-sm font-medium text-gray-50 mb-1 block">
                          Application Name
                        </label>
                        <Field
                          name="appName"
                          type="text"
                          className="form-control disabled:opacity-75"
                          placeholder="Application Name"
                          value={values?.appName}
                          onChange={handleChange}
                          disabled={true}
                        />
                        {touched.name && errors.name && (
                          <div className="text-sm font-medium text-red-500">
                            {errors.name}
                          </div>
                        )}
                      </div>
                      {COLOR_TYPES.map((colorType) => (
                        <div key={colorType}>
                          <label className="text-sm mt-2 font-medium text-gray-50 mb-1 block">
                            Select {convertCamelCaseToCapitalize(colorType)}
                          </label>
                          <div
                            className="form-control relative bg-white flex items-center px-3 cursor-pointer"
                            onClick={() =>
                              setShouldShowColorPicker(!shouldShowColorPicker)
                            }
                          >
                            <input
                              className={classNames(
                                "absolute top-0 left-0 h-full w-full opacity-0 cursor-pointer",
                                { hidden: shouldShowColorPicker }
                              )}
                              type="color"
                              value={values[colorType]}
                              name={colorType}
                              onChange={(e) =>
                                setFieldValue([colorType], e.target.value)
                              }
                            />
                            <div
                              className="w-2 h-2 rounded-full mr-3"
                              style={{
                                backgroundColor: `${values[colorType]}`,
                              }}
                            ></div>
                            <span className="text-gray-900">
                              {values[colorType]}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="form-group">
                        <div className="flex justify-between mb-1">
                          <div className="text-sm font-medium text-gray-50">
                            Select Brand Logo
                          </div>
                          {values.logoWithBrand !== DEFAULT_BRAND_LOGO_URL && (
                            <button
                              type="button"
                              className="rounded-md hover:bg-gray-900"
                              onClick={() =>
                                handleResetImage(setFieldValue, "logoWithBrand")
                              }
                            >
                              <img
                                src={crossIcon}
                                alt="cross"
                                className="w-4"
                              />
                            </button>
                          )}
                        </div>
                        <label>
                          <div className="w-full border-2 border-gray-300 border-dashed rounded-md p-3 grid place-content-center cursor-pointer">
                            <span>
                              <div className="text-center">
                                <Field name="logoWithBrand">
                                  {() => (
                                    <div>
                                      <img
                                        src={brandLogoBase64}
                                        alt="Default brand Logo"
                                        className="flex w-28 h-100"
                                      />
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(event) =>
                                          handleImageUpload(
                                            event,
                                            setFieldValue,
                                            "logoWithBrand"
                                          )
                                        }
                                        onClick={(event) =>
                                          (event.target.value = null)
                                        }
                                      />
                                    </div>
                                  )}
                                </Field>
                              </div>
                            </span>
                          </div>
                        </label>
                      </div>
                      <div className="form-group">
                        <div className="flex justify-between mb-1">
                          <div className="text-sm font-medium text-gray-50">
                            Select Logo
                          </div>
                          {values.logo !== DEFAULT_LOGO_URL && (
                            <button
                              type="button"
                              className="rounded-md hover:bg-gray-900"
                              onClick={() =>
                                handleResetImage(setFieldValue, "logo")
                              }
                            >
                              <img
                                src={crossIcon}
                                alt="cross"
                                className="w-4"
                              />
                            </button>
                          )}
                        </div>
                        <label>
                          <div className="w-full border-2 border-gray-300 border-dashed rounded-md p-3 grid place-content-center cursor-pointer">
                            <span>
                              <div className="text-center">
                                <Field name="logo">
                                  {() => (
                                    <div>
                                      <img
                                        src={appLogoBase64}
                                        alt="Default Logo"
                                        className="flex w-28 h-100"
                                      />
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(event) =>
                                          handleImageUpload(
                                            event,
                                            setFieldValue,
                                            "logo"
                                          )
                                        }
                                        onClick={(event) =>
                                          (event.target.value = null)
                                        }
                                      />
                                    </div>
                                  )}
                                </Field>
                              </div>
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                    <div className="text-right flex justify-end">
                      <ReDeployButton
                        isDeployBtnDisabled={
                          !isValid ||
                          !dirty ||
                          timerActive ||
                          isSubmitButtonDisabled
                        }
                        deployStatus={appDeployStatus?.frontend}
                        deployedAt={deployedAt}
                        handleOnSubmit={handleOnSubmit}
                        show={show}
                        setShow={setShow}
                        data={data}
                        deploymentLogs={deploymentLogs}
                        tooltipPosition="right-32"
                        isApplicationSection={true}
                      />
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="border border-gray-700 mb-4 bg-gray-800 rounded-lg">
          <div className="p-6 border-b border-gray-700">
            <h6 className="text-base font-bold text-gray-50">Delete App</h6>
            <p className="text-sm text-gray-50">
              This action cannot be undone.
            </p>
          </div>
          <div className="p-6">
            <div className="text-right">
              <button
                className="btn btn-danger"
                onClick={() => {
                  dispatch(setSelectedModal(DELETE_MODAL));
                  dispatch(setItemToBeDeleted(appDetails));
                  setShouldShowDeleteModal(true);
                }}
              >
                Permanently Delete
              </button>
            </div>
          </div>
        </div>
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteApp}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Application"
            spinner={DELETE_APPLICATION}
          />
        )}
      </div>
    </div>
  );
};

export default Settings;
